import { useState, useEffect, useRef } from 'react';
import { loadPositions } from './storage';
import { useStockFeed } from './useStockFeed';
import './ETF.css';

/* ── DCA tier table (BD Collins) ── */
const TIERS = [
  { label: 'Within 15% of ATH', min: 0,  max: 15,       pct: 20 },
  { label: '15–25% below ATH',  min: 15, max: 25,        pct: 40 },
  { label: '25–35% below ATH',  min: 25, max: 35,        pct: 60 },
  { label: '35%+ below ATH',    min: 35, max: Infinity,   pct: 80 },
];

function getTier(distPct) {
  return TIERS.find(t => distPct >= t.min && distPct < t.max) ?? TIERS[0];
}

function athDist(current, ath) {
  if (!current || !ath || ath <= 0) return null;
  return Math.max(0, ((ath - current) / ath) * 100);
}

/* ── Leverage ratio helpers ── */
function buildRatioData(positions) {
  const find = name => positions.find(p => p.name.toUpperCase() === name) ?? null;
  const val  = p    => (p ? p.shares * p.currentPrice : 0);
  const tqqq = find('TQQQ');
  const upro = find('UPRO');
  const smia = find('SMIA');
  const schd = find('SCHD');
  const levValue = val(tqqq) + val(upro);
  const defValue = val(smia) + val(schd);
  const total    = levValue + defValue;
  return { tqqq, upro, smia, schd, levValue, defValue, total };
}

/* ── localStorage keys ── */
const DCA_KEY    = 'isa_dca_inputs';
const ANNUAL_KEY = 'isa_annual_target';

function loadDca() {
  try { return JSON.parse(localStorage.getItem(DCA_KEY)) ?? {}; }
  catch { return {}; }
}

function loadAnnualTarget() {
  try { return Number(localStorage.getItem(ANNUAL_KEY)) || 20000; }
  catch { return 20000; }
}

/* ─────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────── */
export default function ETF() {
  const [inp, setInp] = useState(loadDca);

  function set(field, value) {
    const next = { ...inp, [field]: value };
    setInp(next);
    try { localStorage.setItem(DCA_KEY, JSON.stringify(next)); } catch {}
  }

  /* ── Annual target (shared with Re-Entry Analyzer) ── */
  const [annualTarget, setAnnualTarget] = useState(loadAnnualTarget);

  function handleAnnualChange(val) {
    const n = Number(val) || 0;
    setAnnualTarget(n);
    try { localStorage.setItem(ANNUAL_KEY, String(n)); } catch {}
  }

  const monthlySuggested = Math.round(annualTarget / 12);

  /* ── Live price feed for DCA tickers ── */
  const { data: stockData, getStockData } = useStockFeed(['TQQQ', 'UPRO']);

  const inpRef = useRef(inp);
  inpRef.current = inp;

  const autoFilled = useRef(false);
  useEffect(() => {
    if (autoFilled.current) return;
    const tqqqLive = getStockData('TQQQ');
    const uproLive = getStockData('UPRO');
    if (tqqqLive.status !== 'live' && uproLive.status !== 'live') return;

    autoFilled.current = true;
    const cur = inpRef.current;
    const updates = {};
    if (!cur.tqqqPrice && tqqqLive.price) updates.tqqqPrice = tqqqLive.price.toFixed(2);
    if (!cur.tqqqAth   && tqqqLive.ath)   updates.tqqqAth   = tqqqLive.ath.toFixed(2);
    if (!cur.uproPrice && uproLive.price) updates.uproPrice = uproLive.price.toFixed(2);
    if (!cur.uproAth   && uproLive.ath)   updates.uproAth   = uproLive.ath.toFixed(2);

    if (Object.keys(updates).length > 0) {
      const next = { ...cur, ...updates };
      setInp(next);
      try { localStorage.setItem(DCA_KEY, JSON.stringify(next)); } catch {}
    }
  }, [stockData]); // eslint-disable-line react-hooks/exhaustive-deps

  const positions = loadPositions();

  /* ── DCA derived values ── */
  const budget     = Math.max(0, Number(inp.budget) || monthlySuggested);
  const half       = budget / 2;
  const tqqqDist   = athDist(Number(inp.tqqqPrice), Number(inp.tqqqAth));
  const uproDist   = athDist(Number(inp.uproPrice), Number(inp.uproAth));
  const tqqqTier   = tqqqDist !== null ? getTier(tqqqDist) : null;
  const uproTier   = uproDist !== null ? getTier(uproDist) : null;
  const tqqqAlloc  = tqqqTier ? (half * tqqqTier.pct) / 100 : null;
  const uproAlloc  = uproTier ? (half * uproTier.pct) / 100 : null;
  const totalAlloc = (tqqqAlloc ?? 0) + (uproAlloc ?? 0);
  const deployPct  = budget > 0 ? (totalAlloc / budget) * 100 : 0;

  /* 60/40 split of total allocation */
  const tqqq60 = totalAlloc * 0.60;
  const upro40 = totalAlloc * 0.40;

  /* ── Ratio derived values ── */
  const { tqqq, upro, smia, schd, levValue, defValue, total } = buildRatioData(positions);
  const levPct    = total > 0 ? (levValue / total) * 100 : 0;
  const defPct    = total > 0 ? (defValue / total) * 100 : 0;
  const inBalance = total > 0 && levPct >= 75 && levPct <= 85;
  const delta     = total * 0.80 - levValue;

  return (
    <div className="etf-screen">

      {/* ══════════════════════════════════════════
          FEATURE 1 — DCA CALCULATOR
          ══════════════════════════════════════════ */}
      <section className="etf-section">
        <h2 className="etf-section-title">
          <span className="etf-section-icon">💰</span> DCA Calculator
        </h2>

        {/* Annual target + monthly suggestion row */}
        <div className="dca-budget-row">
          <label className="dca-budget-label">Annual Target (€)</label>
          <div className="dca-budget-field">
            <span className="dca-currency">€</span>
            <input
              type="number"
              className="form-input dca-budget-input"
              min="0"
              step="1000"
              value={annualTarget || ''}
              onChange={e => handleAnnualChange(e.target.value)}
              placeholder="20000"
            />
          </div>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
            → <strong style={{ color: '#90caf9' }}>€{monthlySuggested.toLocaleString()}/mo</strong>
          </span>
        </div>

        {/* Monthly capital for this deployment */}
        <div className="dca-budget-row">
          <label className="dca-budget-label">Capital This Month (€)</label>
          <div className="dca-budget-field">
            <span className="dca-currency">€</span>
            <input
              type="number"
              className="form-input dca-budget-input"
              min="0"
              step="100"
              value={inp.budget ?? monthlySuggested}
              onChange={e => set('budget', e.target.value)}
              placeholder={String(monthlySuggested)}
            />
          </div>
          <button
            className="btn btn-ghost"
            style={{ fontSize: '0.72rem', padding: '4px 10px', minHeight: 'unset' }}
            onClick={() => set('budget', String(monthlySuggested))}
          >
            Use suggested
          </button>
        </div>

        {/* Ticker cards */}
        <div className="dca-cards">
          <DcaTickerCard
            ticker="TQQQ"
            half={half}
            priceVal={inp.tqqqPrice ?? ''}
            athVal={inp.tqqqAth   ?? ''}
            onPrice={v => set('tqqqPrice', v)}
            onAth={v   => set('tqqqAth',   v)}
            dist={tqqqDist}
            tier={tqqqTier}
            alloc={tqqqAlloc}
            liveData={getStockData('TQQQ')}
          />
          <DcaTickerCard
            ticker="UPRO"
            half={half}
            priceVal={inp.uproPrice ?? ''}
            athVal={inp.uproAth   ?? ''}
            onPrice={v => set('uproPrice', v)}
            onAth={v   => set('uproAth',   v)}
            dist={uproDist}
            tier={uproTier}
            alloc={uproAlloc}
            liveData={getStockData('UPRO')}
          />
        </div>

        {/* Total summary */}
        {(tqqqTier || uproTier) && (
          <div className="dca-summary">
            <div className="dca-summary-main">
              <span className="dca-summary-label">Total to deploy this month</span>
              <span className="dca-summary-amount">€{totalAlloc.toFixed(0)}</span>
            </div>
            <div className="dca-deploy-bar-wrap">
              <div
                className="dca-deploy-bar"
                style={{ width: `${Math.min(100, deployPct)}%` }}
              />
            </div>
            <div className="dca-summary-sub">
              <span>of €{budget.toFixed(0)} budget</span>
              <span>{deployPct.toFixed(0)}% deployed</span>
            </div>

            {/* 60/40 split suggestion */}
            {totalAlloc > 0 && (
              <div style={{
                display: 'flex', gap: '10px', marginTop: '4px',
                borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '8px',
              }}>
                <div style={{ flex: 1, background: 'rgba(46,117,182,0.2)', borderRadius: '7px', padding: '7px 10px' }}>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: '2px' }}>TQQQ 60%</div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: '#90caf9' }}>€{tqqq60.toFixed(0)}</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(52,211,153,0.1)', borderRadius: '7px', padding: '7px 10px' }}>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: '2px' }}>UPRO 40%</div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: '#6ee7b7' }}>€{upro40.toFixed(0)}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════
          FEATURE 2 — LEVERAGE RATIO MONITOR
          ══════════════════════════════════════════ */}
      <section className="etf-section">
        <h2 className="etf-section-title">
          <span className="etf-section-icon">⚖️</span> Leverage Ratio Monitor
        </h2>

        {total === 0 ? (
          <div className="lrm-empty">
            <span className="lrm-empty-icon">📋</span>
            <p>No TQQQ, UPRO, SMIA, or SCHD positions found in Stop-Loss Manager.</p>
            <p className="lrm-empty-hint">Add them on the Dashboard tab to track your ratio here.</p>
          </div>
        ) : (
          <>
            {/* Breakdown */}
            <div className="lrm-breakdown">
              <PositionGroup
                label="Leveraged"
                target="80%"
                colorClass="lev"
                items={[
                  { ticker: 'TQQQ', pos: tqqq },
                  { ticker: 'UPRO', pos: upro },
                ]}
                groupValue={levValue}
              />
              <PositionGroup
                label="Defensive"
                target="20%"
                colorClass="def"
                items={[
                  { ticker: 'SMIA', pos: smia },
                  { ticker: 'SCHD', pos: schd },
                ]}
                groupValue={defValue}
              />
            </div>

            {/* Gauge */}
            <div className="ratio-gauge">
              <div className="gauge-bar">
                <div
                  className={`gauge-fill gauge-fill--lev${!inBalance ? ' gauge-fill--warn' : ''}`}
                  style={{ width: `${levPct}%` }}
                >
                  {levPct > 12 && <span className="gauge-pct">{levPct.toFixed(1)}%</span>}
                </div>
                <div
                  className="gauge-fill gauge-fill--def"
                  style={{ width: `${defPct}%` }}
                >
                  {defPct > 6 && <span className="gauge-pct">{defPct.toFixed(1)}%</span>}
                </div>
              </div>

              <div className="gauge-marker-row" aria-hidden="true">
                <div className="gauge-marker gauge-marker--lo"  style={{ left: '75%' }} title="Lower bound 75%" />
                <div className="gauge-marker gauge-marker--tgt" style={{ left: '80%' }} title="Target 80%">
                  <span className="gauge-marker-label">80%</span>
                </div>
                <div className="gauge-marker gauge-marker--hi"  style={{ left: '85%' }} title="Upper bound 85%" />
              </div>

              <div className="gauge-legend">
                <span className="gl gl--lev">■ TQQQ + UPRO</span>
                <span className="gl gl--target">│ 80% target (±5%)</span>
                <span className="gl gl--def">■ SMIA + SCHD</span>
              </div>
            </div>

            {/* Status banner */}
            {inBalance ? (
              <div className="lrm-banner lrm-banner--ok">
                ✓ Balanced — {levPct.toFixed(1)} / {defPct.toFixed(1)} is within target range (75:25 to 85:15)
              </div>
            ) : (
              <div className="lrm-banner lrm-banner--warn">
                ⚠ Out of balance — current ratio is {levPct.toFixed(1)} : {defPct.toFixed(1)}
                {' '}(target 80:20, tolerance 75:25 – 85:15)
              </div>
            )}

            {/* Rebalance calculator */}
            {!inBalance && (
              <div className="rebalance-card">
                <h3 className="rebalance-title">Rebalance Calculator</h3>
                <p className="rebalance-subtitle">
                  Total portfolio value: <strong>${total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong>
                  {' '}· Move <strong>${Math.abs(delta).toFixed(0)}</strong> to restore 80:20
                </p>

                {delta > 0 ? (
                  <RebalanceOptions
                    fromLabel="SMIA + SCHD"
                    toLabel="TQQQ + UPRO"
                    amount={delta}
                    direction="increase-lev"
                  />
                ) : (
                  <RebalanceOptions
                    fromLabel="TQQQ + UPRO"
                    toLabel="SMIA + SCHD"
                    amount={Math.abs(delta)}
                    direction="increase-def"
                  />
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/* ─── Sub-components (unchanged) ─── */

function DcaTickerCard({ ticker, half, priceVal, athVal, onPrice, onAth, dist, tier, alloc, liveData }) {
  const liveStatus  = liveData?.status ?? 'loading';
  const livePrice   = liveData?.price ?? null;
  const liveAth     = liveData?.ath   ?? null;
  const updatedAt   = liveData?.updatedAt ?? null;
  const updatedFmt  = updatedAt
    ? updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="dca-card">
      <div className="dca-card-header">
        <span className="dca-ticker">{ticker}</span>
        <div className="dca-header-badges">
          {dist !== null && (
            <span className="dca-dist-badge">{dist.toFixed(1)}% below ATH</span>
          )}
          {liveStatus !== 'loading' && (
            <span className={`live-badge live-badge--${liveStatus}`}>
              <span className="live-dot" />
              {liveStatus === 'live' ? 'Live' : 'Offline'}
            </span>
          )}
        </div>
      </div>

      {liveStatus === 'live' && (
        <div className="dca-live-row">
          <div className="dca-live-item">
            <span className="dca-live-label">Price</span>
            <span className="dca-live-value">${livePrice.toFixed(2)}</span>
            <button
              className="dca-live-use-btn"
              onClick={() => onPrice(livePrice.toFixed(2))}
              title="Use live price"
            >↙ use</button>
          </div>
          {liveAth !== null && (
            <div className="dca-live-item">
              <span className="dca-live-label">52W High</span>
              <span className="dca-live-value">${liveAth.toFixed(2)}</span>
              <button
                className="dca-live-use-btn"
                onClick={() => onAth(liveAth.toFixed(2))}
                title="Use 52-week high as ATH"
              >↙ use</button>
            </div>
          )}
          {updatedFmt && (
            <span className="dca-live-updated">as of {updatedFmt}</span>
          )}
        </div>
      )}

      <div className="dca-card-inputs">
        <label className="form-label">
          Current Price ($)
          <input
            type="number"
            className="form-input"
            min="0"
            step="0.01"
            value={priceVal}
            onChange={e => onPrice(e.target.value)}
            placeholder="0.00"
          />
        </label>
        <label className="form-label">
          All-Time High ($)
          <input
            type="number"
            className="form-input"
            min="0"
            step="0.01"
            value={athVal}
            onChange={e => onAth(e.target.value)}
            placeholder="0.00"
          />
        </label>
      </div>

      {tier && (
        <>
          <div className="tier-strip">
            {TIERS.map(t => (
              <div key={t.label} className={`tier-box${t === tier ? ' tier-box--active' : ''}`}>
                <span className="tier-box-label">{t.label}</span>
                <span className="tier-box-pct">{t.pct}%</span>
              </div>
            ))}
          </div>
          <div className="dca-card-result">
            <span className="dca-result-label">Allocate from ½ budget (${half.toFixed(0)})</span>
            <span className="dca-result-amount">${alloc.toFixed(0)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function PositionGroup({ label, target, colorClass, items, groupValue }) {
  return (
    <div className={`lrm-group lrm-group--${colorClass}`}>
      <div className="lrm-group-header">
        <span className="lrm-group-label">{label}</span>
        <span className="lrm-group-target">target {target}</span>
      </div>
      {items.map(({ ticker, pos }) => {
        const value = pos ? pos.shares * pos.currentPrice : 0;
        return (
          <div key={ticker} className={`lrm-ticker-row${!pos ? ' lrm-ticker-row--missing' : ''}`}>
            <span className="lrm-t-name">{ticker}</span>
            <span className="lrm-t-value">
              {pos
                ? `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                : <em>not in portfolio</em>}
            </span>
          </div>
        );
      })}
      <div className="lrm-group-total">
        Total: ${groupValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

function RebalanceOptions({ fromLabel, toLabel, amount }) {
  return (
    <div className="rebalance-options">
      <div className="rebalance-option rebalance-option--primary">
        <div className="rebalance-opt-arrow rebalance-opt-arrow--buy">↑</div>
        <div>
          <span className="rebalance-opt-action">Buy ${amount.toFixed(0)}</span>
          {' '}more {toLabel}
          <span className="rebalance-opt-split"> (≈ ${(amount / 2).toFixed(0)} each)</span>
        </div>
      </div>
      <div className="rebalance-divider">— or —</div>
      <div className="rebalance-option rebalance-option--secondary">
        <div className="rebalance-opt-arrow rebalance-opt-arrow--sell">↓</div>
        <div>
          <span className="rebalance-opt-action">Sell ${amount.toFixed(0)}</span>
          {' '}from {fromLabel}
          <span className="rebalance-opt-split"> (≈ ${(amount / 2).toFixed(0)} each)</span>
        </div>
      </div>
    </div>
  );
}
